import { ReactElement, Suspense, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import cc from 'classcat';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { fireEvent } from 'utils/generic/event-bus';
import { TAtoZMenuConfigs } from 'interfaces/generic';
import { AtoZMenuDefaultConfigs } from './constants';
import { ESearchElementEventBusActionTypes } from 'newelements/SearchElement/partials/types';
import { AtoZMenuContent } from 'components/AtoZMenuContent';
import { AtoZSkeleton } from 'components/AtoZMenuContent/Skeleton';
import { GlobalIcon } from 'components/GlobalIcon';
import { Popup } from 'components/Popup';
import { getHeaderState } from 'store/selectors/app-data';
import { getQuickSingleBetslipOpen } from 'store/selectors/betslip';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

type TAtoZMenuProps = {
  configs?: TAtoZMenuConfigs;
};

const atoZMenuDefaultProps = {
  configs: AtoZMenuDefaultConfigs
};

export const AtoZMenu = ({
  configs = AtoZMenuDefaultConfigs
}: TAtoZMenuProps = atoZMenuDefaultProps): ReactElement => {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [isRtl] = useState(SpringConfigs.IS_RTL);
  const { isVisible: isHeaderVisible } = useSelector(getHeaderState);
  const isQuickSingleBetslipOpen = useSelector(getQuickSingleBetslipOpen);

  const chunkLoader = () => {
    import(
      /* webpackChunkName: "a-to-z-default-pages" */ 'components/AtoZMenuContent/DefaultPages'
    );
    import(
      /* webpackChunkName: "a-to-z-data-pages" */ 'components/AtoZMenuContent/DataPages'
    );
    import(
      /* webpackChunkName: "a-to-z-menu-content" */ 'components/AtoZMenuContent'
    );
    import(
      /* webpackChunkName: "language-switcher" */ 'components/Shared/LanguageSwitcher'
    );
    import(
      /* webpackChunkName: "sport-page" */ 'components/AtoZMenuContent/SportPage'
    );
    import(
      /* webpackChunkName: "a-to-z-sport-plain-view" */ 'components/AtoZMenuContent/SportListViews/PlainView'
    );
  };

  useEffect(() => {
    if (!location.hash) {
      if (open) {
        document.documentElement.style.overflow = 'hidden';
      } else {
        document.documentElement.style.overflow = 'visible';
      }
    }

    if (!open) {
      fireEvent({ key: ESearchElementEventBusActionTypes.RESET });
    }
  }, [open]);

  useEffect(() => {
    const openAToZ = () => {
      chunkLoader();
      setOpen(true);
    };

    window.openAZmenu = openAToZ;
  }, []);

  return (
    <>
      <GlobalIcon
        lib="generic"
        name="menuBurger"
        theme="default"
        size={EIconSize._24}
        className="AtoZMenu__menu-burger-icon"
        onClick={() => {
          chunkLoader();
          setOpen(true);
        }}
      />
      <Popup
        animationType={isRtl ? 'toLeft' : 'toRight'}
        className={cc([
          'burger-menu-drawer',
          {
            'burger-menu-drawer--withFixedHeader': isHeaderVisible,
            'overlay-index': isQuickSingleBetslipOpen
          }
        ])}
        posRight={isRtl ? '0' : '40px'}
        posLeft={isRtl ? '40px' : '0'}
        open={open}
        onMaskClick={() => setOpen(false)}
        mask
      >
        {open && (
          <div className="AtoZ-closeIconBlock">
            <GlobalIcon
              lib="generic"
              name="close"
              theme="default"
              size={EIconSize._18}
              onClick={() => setOpen(false)}
            />
          </div>
        )}
        {!visible && (
          <div className="a-z-loading">
            <AtoZSkeleton
              includeSearch
              sliderArrowsAToZ={configs.sliderArrowsAToZ}
            />
          </div>
        )}

        <Suspense fallback={null}>
          <AtoZMenuContent
            setVisible={setOpen}
            setOpen={setVisible}
            open={open}
            {...{ configs }}
          />
        </Suspense>
      </Popup>
    </>
  );
};
